import {
  RefreshCw, AlertTriangle, TrendingUp, TrendingDown, Loader2,
  Repeat, Zap, MessageSquare, CheckCircle2, UserCheck, Edit2,
} from 'lucide-react'
import InputHistoryFilters from './InputHistoryFilters'
import { formatAmount, formatShortDate, formatTimestamp } from '../../../utils/format'
import { today as todayStr } from '../../../utils/dates'
import { DUE_DATE_COLUMN_LABEL } from '../../../utils/labels'
import type { LogEntry } from '../types'

// R-9 (2026-05-24): InputPage.tsx から登録履歴セクションを分離（規約 500 行遵守の段階対応）
// InputPage 1547 → ~1267 行に削減。InputPage 自体は state ハブとして残し、表示部のみ extract。

interface CommentCountInfo { total: number; unread: number }

interface ItemInfo { color?: string }

interface CommentTarget {
  vendorId: string
  date: string
  subId: number
  vendorName: string
  amount: number
}

interface Props {
  // データ
  logEntries: LogEntry[]
  filteredLogEntries: LogEntry[]
  loadingRecent: boolean
  aggregate: { income: number; expense: number; net: number }
  todayLogCount: number
  commentCounts: Record<string, CommentCountInfo>
  itemMap: Map<string, ItemInfo>
  // フィルタ state
  searchQuery: string
  setSearchQuery: (v: string) => void
  typeFilter: 'all' | 'income' | 'expense'
  setTypeFilter: (v: 'all' | 'income' | 'expense') => void
  statusFilter: 'all' | 'pending' | 'confirmed'
  setStatusFilter: (v: 'all' | 'pending' | 'confirmed') => void
  recurringFilter: 'all' | 'recurring' | 'onetime'
  setRecurringFilter: (v: 'all' | 'recurring' | 'onetime') => void
  periodDays: number
  setPeriodDays: (v: number) => void
  // ソート
  toggleSort: (key: 'date' | 'amount' | 'vendor' | 'updatedAt') => void
  sortIcon: (key: 'date' | 'amount' | 'vendor' | 'updatedAt') => string
  // アクション state
  confirmingKey: string
  deletingKey: string
  editingKey: string
  // ハンドラ
  loadRecent: () => void
  onShowDuplicate: () => void
  onSelectCommentTarget: (t: CommentTarget) => void
  handleConfirm: (vendorId: string, date: string) => void
  handleStartEdit: (r: LogEntry) => void
}

export default function InputHistoryTable({
  logEntries, filteredLogEntries, loadingRecent, aggregate, todayLogCount,
  commentCounts, itemMap,
  searchQuery, setSearchQuery, typeFilter, setTypeFilter, statusFilter, setStatusFilter,
  recurringFilter, setRecurringFilter, periodDays, setPeriodDays,
  toggleSort, sortIcon,
  confirmingKey, deletingKey, editingKey,
  loadRecent, onShowDuplicate, onSelectCommentTarget, handleConfirm, handleStartEdit,
}: Props) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-2 border-b border-gray-100 bg-gray-50 space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold text-gray-900">自分が登録した取引</h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onShowDuplicate}
              className="flex items-center gap-1 px-2 py-1 text-xs bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-300 rounded transition"
              title="同じ取引先・同じ金額で日付が近い取引を一覧表示"
            >
              <AlertTriangle className="w-3 h-3" />重複チェック
            </button>
            <span className="text-xs text-gray-500 tabular-nums">
              {filteredLogEntries.length !== logEntries.length
                ? `${filteredLogEntries.length} / ${logEntries.length}件`
                : `${logEntries.length}件`}
            </span>
            <button
              onClick={loadRecent}
              disabled={loadingRecent}
              className="text-gray-400 hover:text-gray-700 disabled:opacity-50 transition"
              title="履歴を再取得"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loadingRecent ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
        {/* 検索・フィルタ行 */}
        <InputHistoryFilters
          search={searchQuery}
          onSearch={setSearchQuery}
          type={typeFilter}
          onType={setTypeFilter}
          status={statusFilter}
          onStatus={setStatusFilter}
          recurring={recurringFilter}
          onRecurring={setRecurringFilter}
          periodDays={periodDays}
          onPeriodDays={setPeriodDays}
        />
        {/* 集計バー */}
        {filteredLogEntries.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 text-[11px] pt-0.5">
            <span className="inline-flex items-center gap-1 text-green-700">
              <TrendingUp className="w-3 h-3" />
              収入 <span className="font-mono tabular-nums">¥{aggregate.income.toLocaleString()}</span>
            </span>
            <span className="inline-flex items-center gap-1 text-red-700">
              <TrendingDown className="w-3 h-3" />
              支出 <span className="font-mono tabular-nums">¥{aggregate.expense.toLocaleString()}</span>
            </span>
            <span className={`inline-flex items-center gap-1 ${aggregate.net >= 0 ? 'text-green-800' : 'text-red-800'}`}>
              差引 <span className="font-mono tabular-nums font-semibold">
                {aggregate.net >= 0 ? '+' : ''}¥{aggregate.net.toLocaleString()}
              </span>
            </span>
            <span className="text-gray-500">
              今日登録: <span className="font-semibold text-gray-900 tabular-nums">{todayLogCount}</span>件
            </span>
          </div>
        )}
      </div>
      {loadingRecent ? (
        <div className="px-4 py-8 text-center text-xs text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin inline" /> 読み込み中...
        </div>
      ) : logEntries.length === 0 ? (
        <div className="px-4 py-8 text-center text-xs text-gray-400">
          取得期間内の登録履歴はありません
        </div>
      ) : filteredLogEntries.length === 0 ? (
        <div className="px-4 py-8 text-center text-xs text-gray-400">
          条件に一致する取引が見つかりません
          <div className="mt-2">
            <button
              type="button"
              onClick={() => { setSearchQuery(''); setTypeFilter('all'); setStatusFilter('all'); setRecurringFilter('all') }}
              className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-600 rounded transition"
            >
              条件をクリア
            </button>
          </div>
        </div>
      ) : (
        <div className="max-h-[60vh] overflow-auto">
          <table className="w-full text-[12px] border-collapse">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr className="border-b border-gray-200 text-gray-600">
                <th
                  className="px-2 py-1.5 text-left font-medium whitespace-nowrap cursor-pointer select-none hover:bg-gray-100"
                  onClick={() => toggleSort('date')}
                  title="クリックでソート"
                >請求日{sortIcon('date')}</th>
                <th className="px-2 py-1.5 text-left font-medium whitespace-nowrap">{DUE_DATE_COLUMN_LABEL}</th>
                <th className="px-2 py-1.5 text-left font-medium whitespace-nowrap">対象月</th>
                <th className="px-2 py-1.5 text-center font-medium whitespace-nowrap">種別</th>
                <th className="px-2 py-1.5 text-left font-medium whitespace-nowrap">部門</th>
                <th className="px-2 py-1.5 text-left font-medium whitespace-nowrap">品目</th>
                <th className="px-2 py-1.5 text-left font-medium whitespace-nowrap">カテゴリ</th>
                <th
                  className="px-2 py-1.5 text-left font-medium cursor-pointer select-none hover:bg-gray-100"
                  onClick={() => toggleSort('vendor')}
                  title="クリックでソート"
                >取引先{sortIcon('vendor')}</th>
                <th
                  className="px-2 py-1.5 text-right font-medium whitespace-nowrap cursor-pointer select-none hover:bg-gray-100"
                  onClick={() => toggleSort('amount')}
                  title="クリックでソート"
                >金額(税込){sortIcon('amount')}</th>
                <th className="px-2 py-1.5 text-left font-medium">メモ</th>
                <th
                  className="px-2 py-1.5 text-left font-medium whitespace-nowrap cursor-pointer select-none hover:bg-gray-100"
                  onClick={() => toggleSort('updatedAt')}
                  title="クリックでソート"
                >入力日{sortIcon('updatedAt')}</th>
                <th className="px-2 py-1.5 text-left font-medium whitespace-nowrap">登録者</th>
                <th className="px-2 py-1.5 text-left font-medium whitespace-nowrap">確認者</th>
                <th className="px-2 py-1.5 text-left font-medium whitespace-nowrap">確認日</th>
                <th className="px-2 py-1.5 text-center font-medium whitespace-nowrap">💬</th>
                <th className="px-2 py-1.5 text-center font-medium whitespace-nowrap sticky right-[72px] bg-gray-50 z-20 shadow-[-2px_0_2px_-2px_rgba(0,0,0,0.08)]">状態</th>
                <th className="px-2 py-1.5 text-center font-medium whitespace-nowrap sticky right-0 bg-gray-50 z-20">編集</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogEntries.map((r) => {
                const isConfirmed = !!r.confirmedByName
                const isConfirming = confirmingKey === r.key
                const isDeleting = deletingKey === r.key
                const isEditingRow = editingKey === r.key
                const rowBg = isEditingRow ? 'bg-amber-50' : isConfirmed ? 'bg-green-50/30' : 'bg-white'
                return (
                  <tr
                    key={r.key}
                    id={`log-row-${r.key.replace(/[^a-zA-Z0-9_-]/g, '_')}`}
                    className={`border-b border-gray-100 hover:bg-gray-50 group ${
                      isEditingRow ? 'ring-2 ring-amber-400 bg-amber-50' : ''
                    } ${!isEditingRow ? rowBg : ''}`}
                  >
                    <td className="px-1.5 py-1.5 tabular-nums text-gray-700 whitespace-nowrap" title={r.date}>{formatShortDate(r.date)}</td>
                    <td className="px-1.5 py-1.5 tabular-nums whitespace-nowrap" title={r.dueDate || ''}>
                      {r.dueDate ? (
                        <span className={r.dueDate < todayStr() && !isConfirmed ? 'text-red-600 font-medium' : 'text-gray-700'}>
                          {formatShortDate(r.dueDate)}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-1.5 py-1.5 tabular-nums whitespace-nowrap text-gray-700" title={r.targetMonth || ''}>
                      {r.targetMonth ? r.targetMonth.replace('-', '/') : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-2 py-1.5 text-center whitespace-nowrap">
                      {r.isRecurring ? (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-blue-100 text-blue-800 text-[10px] font-medium rounded border border-blue-300" title="定期取引">
                          <Repeat className="w-2.5 h-2.5" />定期
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-slate-100 text-slate-600 text-[10px] rounded border border-slate-200" title="単発">
                          <Zap className="w-2.5 h-2.5" />単発
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-gray-700 whitespace-nowrap max-w-[180px] truncate" title={r.departmentPath || '—'}>
                      {r.departmentPath || <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-2 py-1.5 text-gray-700 whitespace-nowrap max-w-[140px] truncate" title={r.itemName || '—'}>
                      {r.itemName ? (
                        <span className="inline-flex items-center gap-1">
                          <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: itemMap.get(r.itemId)?.color || '#9ca3af' }} />
                          {r.itemName}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <span className={r.type === 'income' ? 'text-green-700' : 'text-red-700'}>
                        {r.categoryName}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-gray-900 max-w-[260px] truncate" title={r.vendorName}>
                      <span className="inline-flex items-center gap-1">
                        {r.isSecret && <span className="text-purple-600 text-[10px]" title="シークレット取引">🔒</span>}
                        <span>{r.vendorName}</span>
                      </span>
                    </td>
                    <td className={`px-2 py-1.5 tabular-nums font-mono text-right whitespace-nowrap ${r.isSecretMasked ? 'text-purple-500' : (r.type === 'income' ? 'text-green-700' : 'text-red-700')}`}>
                      {r.isSecretMasked ? '***' : (<>{r.type === 'expense' && '−'}{formatAmount(r.amount)}</>)}
                    </td>
                    <td className="px-2 py-1.5 text-gray-500 max-w-[280px] truncate" title={r.isSecretMasked ? '(シークレット)' : r.note}>
                      {r.isSecretMasked ? <span className="text-purple-400">(シークレット)</span> : (r.note || '—')}
                    </td>
                    <td className="px-1.5 py-1.5 text-[11px] text-gray-500 tabular-nums whitespace-nowrap">
                      {r.updatedAt ? formatTimestamp(r.updatedAt) : '—'}
                    </td>
                    <td className="px-1.5 py-1.5 text-[11px] text-gray-700 whitespace-nowrap">
                      {r.updatedByName || <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-1.5 py-1.5 text-[11px] text-gray-700 whitespace-nowrap">
                      {isConfirmed ? r.confirmedByName : '—'}
                    </td>
                    <td className="px-1.5 py-1.5 text-[11px] text-gray-500 tabular-nums whitespace-nowrap">
                      {isConfirmed && r.confirmedAt ? formatTimestamp(r.confirmedAt) : '—'}
                    </td>
                    <td className="px-2 py-1.5 text-center whitespace-nowrap">
                      {(() => {
                        const cnt = commentCounts[r.key] || { total: 0, unread: 0 }
                        const hasUnread = cnt.unread > 0
                        return (
                          <button
                            type="button"
                            onClick={() => onSelectCommentTarget({
                              vendorId: r.vendorId,
                              date: r.date,
                              subId: r.subId,
                              vendorName: r.vendorName,
                              amount: r.type === 'expense' ? -r.amount : r.amount,
                            })}
                            className={`inline-flex items-center justify-center w-6 h-6 rounded transition relative ${
                              hasUnread ? 'text-red-600 hover:bg-red-100' :
                              cnt.total > 0 ? 'text-blue-600 hover:bg-blue-100' :
                              'text-gray-400 hover:bg-gray-100'
                            }`}
                            title={
                              hasUnread ? `未読 ${cnt.unread}件 / 全${cnt.total}件` :
                              cnt.total > 0 ? `コメント ${cnt.total}件（既読）` :
                              'コメントを追加'
                            }
                          >
                            <MessageSquare className="w-3.5 h-3.5" />
                            {cnt.total > 0 && (
                              <span className={`absolute -top-1 -right-1 min-w-[16px] h-4 px-1 text-white text-[9px] font-bold rounded-full flex items-center justify-center ${
                                hasUnread ? 'bg-red-600' : 'bg-blue-600'
                              }`}>
                                {hasUnread ? (cnt.unread > 99 ? '99+' : cnt.unread) : (cnt.total > 99 ? '99+' : cnt.total)}
                              </span>
                            )}
                          </button>
                        )
                      })()}
                    </td>
                    <td className={`px-2 py-1.5 text-center whitespace-nowrap sticky right-[72px] ${rowBg} group-hover:bg-gray-50 z-10 shadow-[-2px_0_2px_-2px_rgba(0,0,0,0.08)]`}>
                      {isConfirmed ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-800 text-[11px] font-medium rounded border border-green-300">
                          <CheckCircle2 className="w-3 h-3" />完了
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleConfirm(r.vendorId, r.date)}
                          disabled={isConfirming}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white font-medium rounded transition"
                          title="確認済みにする"
                        >
                          {isConfirming ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserCheck className="w-3 h-3" />}
                          {isConfirming ? '…' : '確認'}
                        </button>
                      )}
                    </td>
                    <td className={`px-2 py-1.5 text-center whitespace-nowrap sticky right-0 ${rowBg} group-hover:bg-gray-50 z-10`}>
                      <button
                        type="button"
                        onClick={() => handleStartEdit(r)}
                        disabled={isDeleting || isEditingRow}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] text-amber-700 hover:bg-amber-100 disabled:text-gray-300 disabled:hover:bg-transparent border border-amber-300 rounded transition"
                        title={isEditingRow ? '編集中' : isConfirmed ? '編集（内容を変えると再確認が必要になります）' : '編集（削除もここから）'}
                      >
                        <Edit2 className="w-3 h-3" />編集
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
